// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What the viewer has asked the arcade to show them (11 §6, M9-50).
//
// 11 §6's table has one line about age: *"Authors declare content rating; the arcade filters by the
// viewer's account settings."* The author's half has always existed — `meta.contentRating` is a
// **required** field of every machine and the format has allowed exactly `everyone` and `teen`
// since it was written. The viewer's half did not. `visibleAtRating` was written at M9-33 with a
// comment explaining the rule and had **no caller anywhere in the product**, so every viewer saw
// every machine whatever they had asked for, and nothing had anywhere to ask them.
//
// **This is a filter, not an age gate, and the difference decides the default.** The product has no
// age verification and never will — 10 §0 forbids this application from creating an identity at all
// for anonymous play, and an arcade that demanded a birthday before it would show you a pinball
// table would be collecting exactly the data it has spent nine milestones not collecting. What it
// has instead is a **13+ rating for the whole product** (01 §Positioning, matching the Chess VR
// Horizon Store posture), so every viewer is already presumed to be a teenager or older. `teen` —
// see everything — is therefore the default, and `everyone` is a gentler arcade somebody chooses:
// a player who wants one, or a parent setting up a child's headset.
//
// Defaulting the other way is the tempting mistake. It reads as the cautious option and it is not
// caution, it is half the catalogue silently missing for every adult who never opened this page,
// with no message anywhere saying why.
//
// **The same value is sent to the server and applied again here.** The Edge Function filters the
// roster before it answers, so the response never carries what the viewer excluded; the client
// filters what it holds, because a roster can be cached and a client can be an older build that
// never sends the parameter. Both call the one rule in `server/ts/machines.ts` — see
// `visibleRoster` — so they cannot drift.

import { readLocal, removeLocal, safeStorage, writeLocal, type StorageLike } from '../storage/local'
import { stampPreference } from '../storage/prefStamps'
import type { RatingAllowed } from '@server/moderation'

/** `'everyone'` is the player's own answer; absent means the product's own 13+ rating. */
export const RATING_KEY = 'hcc.pinball.content-rating'

/** The two the machine format allows, which are the two a viewer can ask for. */
export const RATING_CHOICES: readonly RatingAllowed[] = ['everyone', 'teen']

/**
 * The widest rating this browser has been asked to show, or `teen`.
 *
 * Deliberately not `ratingAllowed(readLocal(...))`: that function reads a value off the **wire**
 * and this one reads a value this build wrote, and giving them one implementation would mean a
 * change to what the arcade accepts from a query string silently changing what a player's own
 * stored choice means. They agree today and they answer different questions.
 */
export function readRatingChoice(storage: StorageLike | undefined = safeStorage()): RatingAllowed {
  return readLocal(storage, RATING_KEY) === 'everyone' ? 'everyone' : 'teen'
}

/**
 * Store the choice, and stamp it either way.
 *
 * `teen` is stored as an **absence**, exactly as `system` is for the flashing control and for the
 * same reason: it is the default, and a browser that has never been told otherwise should hold
 * nothing. The stamp is written before the branch because *choosing the default is a choice* — 07
 * §7's tombstone argument — and without it an account would keep an older `everyone` and hand it
 * straight back at the next sign-in, turning a filter the player had just switched off back on.
 */
export function writeRatingChoice(
  choice: RatingAllowed,
  storage: StorageLike | undefined = safeStorage(),
): void {
  stampPreference('rating', storage)
  if (choice === 'everyone') writeLocal(storage, RATING_KEY, 'everyone')
  else removeLocal(storage, RATING_KEY)
}
